import classNames from 'classnames';
import { take } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { TYPES } from 'components/Button';
import { largeUp, mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import { APPEARANCES } from 'utils/Filters';
import I18n from 'utils/I18n';

import ColorOptions from './ColorOptions';
import { INITIAL_NUMBER_OF_OPTIONS } from './constants';
import Options from './Options';
import Placeholder from './Placeholder';
import SizeOptions from './SizeOptions';

export { APPEARANCES };

const CollectionFilterSelector = ({
  filterIdentifier,
  isExpanded,
  isLoading,
  options,
  onExpand,
  onFilterOptionSelect,
  appearance,
}) => {
  const shownOptions = isExpanded ? options : take(options, INITIAL_NUMBER_OF_OPTIONS);

  return isLoading ? (
    <Placeholder />
  ) : (
    <div className="CollectionFilterSelector">
      <span className="CollectionFilterSelector__heading">
        {I18n.localize(`productAttributes.${filterIdentifier}.label`)}
      </span>

      <div className="CollectionFilterSelector__list">
        {appearance === APPEARANCES.DEFAULT && (
          <div className="CollectionFilter__options">
            <Options
              filterIdentifier={filterIdentifier}
              options={shownOptions}
              onFilterOptionSelect={onFilterOptionSelect}
            />
          </div>
        )}

        {appearance === APPEARANCES.COLOR_SWATCHES && (
          <div className="CollectionFilter__options">
            <ColorOptions
              filterIdentifier={filterIdentifier}
              options={shownOptions}
              onFilterOptionSelect={onFilterOptionSelect}
            />
          </div>
        )}

        {appearance === APPEARANCES.SIZE_BUTTONS && (
          <div className="CollectionFilter__options">
            <SizeOptions
              filterIdentifier={filterIdentifier}
              options={options}
              onFilterOptionSelect={onFilterOptionSelect}
            />
          </div>
        )}
      </div>
      {!isExpanded &&
        options.length > INITIAL_NUMBER_OF_OPTIONS &&
        appearance !== APPEARANCES.SIZE_BUTTONS && (
          <Button
            className={classNames('CollectionFilterSelector__button')}
            type={TYPES.LINK}
            onClick={onExpand}
          >
            {I18n.localize('collectionPage.loadMore.seeAll')} ({options.length})
          </Button>
        )}
      <style jsx>
        {`
          .CollectionFilterSelector__list {
            padding: ${Theme.get('spacing') * 2}px 0 ${Theme.get('spacing') * 1.5}px;
          }

          .CollectionFilterSelector__heading {
            display: block;
            font-size: ${Theme.get('font-size-description-px')};
            font-weight: ${Theme.get('font-weight-bold')};
            line-height: 1.5;
            letter-spacing: 2px;
            text-transform: uppercase;
            margin: 0;
            padding: 0;
          }

          @media ${mediumUp} {
            .CollectionFilterSelector__heading {
              font-size: 16px;
              line-height: 1.5;
            }
          }

          @media ${largeUp} {
            .CollectionFilterSelector__heading {
              font-size: 16px;
              line-height: 1.5;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .CollectionFilterSelector__button.Button {
            letter-spacing: 0.5px;
          }
        `}
      </style>
    </div>
  );
};

CollectionFilterSelector.propTypes = {
  filterIdentifier: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
  isLoading: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      option: PropTypes.string.isRequired,
      value: PropTypes.bool.isRequired,
    })
  ).isRequired,

  onExpand: PropTypes.func,
  onFilterOptionSelect: PropTypes.func,
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
};

CollectionFilterSelector.defaultProps = {
  isExpanded: false,
  isLoading: false,
  onFilterOptionSelect: () => {},
  onExpand: () => {},
  appearance: APPEARANCES.DEFAULT,
};

export default CollectionFilterSelector;
